// Finishing a sign-in that started at a venue's PC (the station sign-in handoff). The PC opens this page in a
// locked-down browser window, or shows a code that opens it on the player's phone, with a single-use secret in the
// fragment. The page:
//
//   1. takes the secret out of the URL and keeps it in this tab only (handoff-token.ts);
//   2. asks which venue and PC it signs in to, and says so in the heading before anything else happens, so a code
//      shown out of context is caught by the player;
//   3. on a phone (no `?via=`), signed out, offers the sign-in page's Google, Discord and Steam buttons here, the
//      sign-in page itself and sign-up, every one of them coming back here; a new account from a provider passes
//      through "Create your Clavian account" and "Complete your profile" on its way back;
//   4. on a phone, signed in, asks them to confirm, and signs them in at that PC with their own session.
//
// In the PC's own sign-in window (`?via=google`, the provider the player tapped at the PC) nothing is asked: the page
// goes straight to that provider ("Taking you to Google…"), and once it comes back signed in it signs the PC in by
// itself and signs this window's browser out. A new account still passes through "Create your Clavian account" and
// "Complete your profile" on the way; only a refusal stops on this page, in words, with a way to try again.
//
// Every failure is said in words; no raw code reaches the screen.

import type { AuthSessionState, SignInHandoffView, SocialProvider } from '@clavian/api-client';
import { ApiTransportError } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, SocialButton, Spinner } from '@clavian/ui-web';
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { PROBLEM, problemCode, problemStatus } from '../api/problems';
import { queryKeys, useSession, useSocialProviders } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { AuthLayout } from '../auth/AuthLayout';
import { Failure } from '../auth/form-controls';
import {
  CreateAccountDialog,
  isSocialProvider,
  SocialButtons,
  SocialDivider,
  socialRefusal,
  useStartSocial,
  withQuery,
  type SocialRoundTrip,
} from '../auth/social';
import { forgetToken, HANDOFF_LIFETIME_MS, rememberToken, resolveHandoffToken } from './handoff-token';

/** The providers a PC offers as a button (the contract's `StationBrowserSignInProvider`). */
export const STATION_SIGN_IN_PROVIDERS = ['google', 'discord', 'steam'] as const;
export type StationSignInProvider = (typeof STATION_SIGN_IN_PROVIDERS)[number];

export const isStationSignInProvider = (value: unknown): value is StationSignInProvider =>
  typeof value === 'string' && (STATION_SIGN_IN_PROVIDERS as readonly string[]).includes(value);

/** Never holds the secret: the page looks up one handoff at a time. */
const LOOKUP_KEY = ['station-sign-in', 'lookup'] as const;

/** The query keys this page owns, kept when "Use a different account" clears the signed-in player's data. */
const ownsKey = (key: readonly unknown[]): boolean => key[0] === 'station-sign-in';

type FailureKind =
  | 'missing'
  | 'invalid'
  | 'unavailable'
  | 'banned'
  | 'signUpDisabled'
  | 'notOnSite'
  | 'rateLimited'
  | 'network'
  | 'generic'
  | 'unauthenticated';

/** A failure that ends this handoff for good: the secret is forgotten and nothing is offered but the explanation. */
const TERMINAL: ReadonlySet<FailureKind> = new Set(['missing', 'invalid', 'unavailable', 'banned', 'signUpDisabled']);

export function classifyHandoffFailure(error: unknown): FailureKind {
  const status = problemStatus(error) ?? (error instanceof ApiTransportError ? error.status : undefined);
  switch (problemCode(error)) {
    case PROBLEM.signInHandoffUnavailable:
      return 'unavailable';
    case PROBLEM.playerBanned:
      return 'banned';
    case PROBLEM.signUpDisabled:
      return 'signUpDisabled';
    case PROBLEM.notOnSite:
      return 'notOnSite';
    case PROBLEM.rateLimited:
      return 'rateLimited';
    case PROBLEM.validationFailed:
      return 'invalid';
    case PROBLEM.unauthenticated:
      return 'unauthenticated';
    default:
      break;
  }
  if (status === undefined) return 'network';
  if (status === 429) return 'rateLimited';
  if (status === 409) return 'unavailable';
  if (status === 400) return 'invalid';
  if (status === 401) return 'unauthenticated';
  return 'generic';
}

const FAILURE_COPY: Record<Exclude<FailureKind, 'unauthenticated'>, { readonly title: string; readonly body: string }> = {
  missing: { title: 'stationSignIn.failure.missingTitle', body: 'stationSignIn.failure.missing' },
  invalid: { title: 'stationSignIn.failure.invalidTitle', body: 'stationSignIn.failure.missing' },
  unavailable: { title: 'stationSignIn.failure.unavailableTitle', body: 'stationSignIn.failure.unavailable' },
  banned: { title: 'stationSignIn.failure.bannedTitle', body: 'stationSignIn.failure.banned' },
  signUpDisabled: { title: 'stationSignIn.failure.signUpDisabledTitle', body: 'stationSignIn.failure.signUpDisabled' },
  // Not for good: on the venue's Wi-Fi, the same press works.
  notOnSite: { title: 'stationSignIn.failure.notOnSiteTitle', body: 'stationSignIn.failure.notOnSite' },
  rateLimited: { title: 'stationSignIn.failure.rateLimitedTitle', body: 'stationSignIn.failure.rateLimited' },
  network: { title: 'stationSignIn.failure.networkTitle', body: 'stationSignIn.failure.network' },
  generic: { title: 'stationSignIn.failure.genericTitle', body: 'stationSignIn.failure.generic' },
};

type Phase =
  | { readonly kind: 'active' }
  | { readonly kind: 'done'; readonly view: SignInHandoffView }
  | { readonly kind: 'cancelled' }
  | { readonly kind: 'failed'; readonly failure: Exclude<FailureKind, 'unauthenticated'> };

/** mm:ss, for the link's countdown. */
export function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * When the link stops working, by this device's clock — or `null` when this device's clock and the server's disagree
 * too much to count down honestly (more than the handoff's whole life left, or none at all, at the moment the server
 * said it was alive). The server stays the authority either way.
 */
function plausibleDeadline(expiresAt: string, receivedAt: number): number | null {
  const deadline = Date.parse(expiresAt);
  if (Number.isNaN(deadline)) return null;
  const left = deadline - receivedAt;
  return left > 5_000 && left <= HANDOFF_LIFETIME_MS + 5_000 ? deadline : null;
}

/** The time, ticking every second until `until` (epoch ms) has passed; still while `until` is `null`. */
function useNow(until: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null) return undefined;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= until) window.clearInterval(timer);
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [until]);
  return now;
}

/** Moves focus to the page's heading whenever what the page is saying changes (not on first render). */
function useFocusHeadingOn(key: string): void {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const heading = document.querySelector<HTMLElement>('#pw-main h1');
    heading?.focus();
  }, [key]);
}

const accountName = (session: AuthSessionState): string => {
  const user = session?.user;
  if (user == null) return '';
  return [user.name, user.username, user.email].find((value) => typeof value === 'string' && value.trim() !== '') ?? '';
};

export function StationSignInPage(): ReactNode {
  const { t } = useTranslation('player');
  const problem = useProblem();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const search = useSearch({ from: '/station-sign-in' });
  const session = useSession();

  // Read once, on arrival: the fragment, else what this tab kept from before a sign-in round trip.
  const [tokenState] = useState(() => resolveHandoffToken(location.hash));
  const token = tokenState.kind === 'ok' ? tokenState.token : null;
  // Opened by the PC's own sign-in window (`?via=`, or a kept secret that was): the confirm asks the server to sign this
  // browser out once the PC is signed in, so no web session is left in it. A phone sends nothing and stays signed in.
  const [fromWindow] = useState(() => search.via !== undefined || (tokenState.kind === 'ok' && tokenState.fromWindow));
  // What the player's own actions (or the server's answers to them) have settled. The page's phase is this, or a
  // failure the lookup or the clock brings about by itself.
  const [settled, setSettled] = useState<Phase>(() =>
    tokenState.kind === 'ok' ? { kind: 'active' } : { kind: 'failed', failure: tokenState.kind },
  );

  // Take the secret out of the visible URL at once (and out of history), keeping it in this tab only.
  const hadFragment = location.hash !== '';
  useEffect(() => {
    if (tokenState.kind === 'ok' && tokenState.fromFragment) rememberToken(tokenState.token, Date.now() + HANDOFF_LIFETIME_MS, fromWindow);
    if (hadFragment) void navigate({ to: '/station-sign-in', search: (previous) => previous, replace: true, resetScroll: false });
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookup = useQuery<SignInHandoffView>({
    queryKey: LOOKUP_KEY,
    queryFn: () => api.lookUpSignInHandoff({ body: { token: token ?? '' } }),
    enabled: token !== null && settled.kind === 'active',
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const deadline = lookup.data === undefined ? null : plausibleDeadline(lookup.data.expiresAt, lookup.dataUpdatedAt);
  const now = useNow(settled.kind === 'active' ? deadline : null);

  // The server's answer sets how long the kept secret lives.
  useEffect(() => {
    if (token !== null && lookup.data !== undefined) rememberToken(token, deadline ?? Date.now() + HANDOFF_LIFETIME_MS, fromWindow);
  }, [token, lookup.data, deadline, fromWindow]);

  const lookupFailure = lookup.isError ? classifyHandoffFailure(lookup.error) : null;
  let phase: Phase = settled;
  if (settled.kind === 'active') {
    // A lookup that fails for good ends the handoff; so does the countdown running out here (say so, rather than let
    // a press fail).
    if (lookupFailure !== null && lookupFailure !== 'unauthenticated' && TERMINAL.has(lookupFailure)) {
      phase = { kind: 'failed', failure: lookupFailure };
    } else if (deadline !== null && now >= deadline) {
      phase = { kind: 'failed', failure: 'unavailable' };
    }
  }

  // A handoff that has ended for good leaves nothing behind in this tab.
  const endedForGood =
    phase.kind === 'done' || phase.kind === 'cancelled' || (phase.kind === 'failed' && TERMINAL.has(phase.failure));
  useEffect(() => {
    if (endedForGood) forgetToken();
  }, [endedForGood]);

  const confirm = useMutation<SignInHandoffView, unknown>({
    mutationFn: () => api.confirmSignInHandoff({ body: { token: token ?? '', ...(fromWindow ? { signOutAfter: true } : {}) } }),
    onSuccess: (view) => {
      forgetToken();
      // The server has signed this window's browser out: nothing of the player's is kept on this page either.
      if (fromWindow) {
        queryClient.removeQueries({ predicate: (query) => !ownsKey(query.queryKey) });
        queryClient.setQueryData(queryKeys.session, null);
      }
      setSettled({ kind: 'done', view });
    },
    onError: (error) => {
      const failure = classifyHandoffFailure(error);
      // The session ended elsewhere: back to "sign in", the secret kept.
      if (failure === 'unauthenticated') void queryClient.invalidateQueries({ queryKey: queryKeys.session });
      else if (TERMINAL.has(failure)) setSettled({ kind: 'failed', failure });
    },
  });

  const signOut = useMutation({
    mutationFn: () => api.signOut(),
    onSuccess: () => {
      queryClient.removeQueries({ predicate: (query) => !ownsKey(query.queryKey) });
      queryClient.setQueryData(queryKeys.session, null);
      confirm.reset();
    },
  });

  const cancel = (): void => {
    forgetToken();
    setSettled({ kind: 'cancelled' });
  };

  const provider = search.via ?? lookup.data?.provider;
  const confirmFailure = confirm.isError ? classifyHandoffFailure(confirm.error) : null;

  // ---- The PC's own sign-in window: straight to the provider, and the PC signed in by itself on the way back ----
  const { providers: socialOn, isPending: socialPending } = useSocialProviders();
  const social = useStartSocial();
  // The provider tapped at the PC: on arrival `?via=`; after a refused round trip `?provider=`; else what the handoff says.
  const returnedProvider = isStationSignInProvider(search.provider) ? search.provider : undefined;
  const windowProvider = fromWindow ? (search.via ?? returnedProvider ?? lookup.data?.provider) : undefined;
  const settledLookup = phase.kind === 'active' && lookupFailure === null && lookup.data !== undefined && !session.isPending && !session.isError;
  // Arriving from the PC (`?via=`, nothing refused yet) with that provider on: off to it at once, once, signed out.
  const goStraight = search.via !== undefined && search.error === undefined && (socialPending || socialOn.includes(search.via));
  const startedTrip = useRef(false);
  useEffect(() => {
    if (!goStraight || socialPending || !settledLookup || session.data != null || search.via === undefined || startedTrip.current) return;
    startedTrip.current = true;
    void social.start(search.via, stationSignInTrip());
  }, [goStraight, socialPending, settledLookup, session.data, search.via, social]);
  // Back from the provider (or "Complete your profile") signed in: confirm by itself, once, signing this browser out.
  const confirmedOnce = useRef(false);
  useEffect(() => {
    if (!fromWindow || !settledLookup || session.data == null || confirmedOnce.current) return;
    confirmedOnce.current = true;
    confirm.mutate();
  }, [fromWindow, settledLookup, session.data, confirm]);

  // What the page is saying, for focus management.
  let stateKey: string;
  if (phase.kind !== 'active') stateKey = phase.kind === 'failed' ? `failed:${phase.failure}` : phase.kind;
  else if (lookupFailure !== null) stateKey = `lookup:${lookupFailure}`;
  else if (lookup.data === undefined || session.isPending) stateKey = 'checking';
  else if (session.isError) stateKey = 'session-error';
  else stateKey = session.data == null ? (fromWindow ? `window:${social.failed ?? social.starting ?? 'out'}` : 'signed-out') : 'confirm';
  useFocusHeadingOn(stateKey);

  // ---- Ended ----
  if (phase.kind === 'done') {
    return (
      <StationSignInLayout title={t('stationSignIn.done.title', { station: phase.view.stationName })}>
        <p className="pw-handoff__body" role="status">
          {t('stationSignIn.done.body')}
        </p>
      </StationSignInLayout>
    );
  }
  if (phase.kind === 'cancelled') {
    return (
      <StationSignInLayout title={t('stationSignIn.cancelled.title')}>
        <p className="pw-handoff__body" role="status">
          {t('stationSignIn.cancelled.body')}
        </p>
      </StationSignInLayout>
    );
  }
  if (phase.kind === 'failed') {
    const canSwitch = phase.failure === 'signUpDisabled' && session.data != null;
    return (
      <FailureState
        failure={phase.failure}
        action={
          canSwitch ? (
            <Button
              variant="secondary"
              block
              busy={signOut.isPending}
              onClick={() => {
                signOut.mutate();
              }}
            >
              {t('stationSignIn.confirm.switchAccount')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  // ---- Looking it up ----
  if (lookupFailure !== null) {
    // Terminal failures are moved to `failed` by the effect above; what is left here can be tried again.
    const retryable = lookupFailure === 'unauthenticated' ? 'generic' : lookupFailure;
    return <FailureState failure={retryable} error={lookup.error} onRetry={() => void lookup.refetch()} />;
  }
  if (lookup.data === undefined || session.isPending) {
    return (
      <StationSignInLayout title={t('stationSignIn.checking')}>
        <p className="pw-handoff__checking" role="status">
          <Spinner size="sm" labelHidden label={t('app.loading')} />
          <span>{t('stationSignIn.checking')}</span>
        </p>
      </StationSignInLayout>
    );
  }
  if (session.isError) {
    return <FailureState failure="network" onRetry={() => void session.refetch()} />;
  }

  const view = lookup.data;
  const expiry =
    deadline === null
      ? t('stationSignIn.expiresSoon')
      : t('stationSignIn.expiresIn', { time: formatRemaining(deadline - now) });

  // ---- The PC's window: no choice page, no confirm button ----
  if (fromWindow && windowProvider !== undefined && session.data == null && (goStraight || search.error !== undefined)) {
    const name = t(`social.provider.${windowProvider}`);
    if (goStraight && social.failed === null) {
      return (
        <StationSignInLayout title={t('stationSignIn.window.redirecting', { provider: name })}>
          <p className="pw-handoff__checking" role="status">
            <Spinner size="sm" labelHidden label={t('app.loading')} />
            <span>{t('stationSignIn.window.redirecting', { provider: name })}</span>
          </p>
        </StationSignInLayout>
      );
    }
    return (
      <StationSignInLayout title={t('stationSignIn.window.retryTitle', { provider: name })}>
        <div className="pw-form">
          <WindowRetry provider={windowProvider} error={search.error} returned={returnedProvider} social={social} />
        </div>
      </StationSignInLayout>
    );
  }
  if (fromWindow && session.data != null) {
    return <WindowConfirm stationName={view.stationName} confirm={confirm} failure={confirmFailure} />;
  }

  // ---- Signed out: sign in, and come back here ----
  if (session.data == null) {
    return (
      <StationSignInLayout title={t('stationSignIn.review', { station: view.stationName, venue: view.venueName })} subtitle={<Expiry text={expiry} />}>
        <div className="pw-form">
          <p className="pw-handoff__body">{t('stationSignIn.signedOut.body')}</p>
          <SignInWays provider={provider} error={search.error} returned={search.provider} />
        </div>
      </StationSignInLayout>
    );
  }

  // ---- Signed in: confirm ----
  const transientFailure =
    confirmFailure !== null && !TERMINAL.has(confirmFailure) && confirmFailure !== 'unauthenticated'
      ? confirmFailure === 'generic'
        ? problem.text(confirm.error, { fallback: t(FAILURE_COPY[confirmFailure].body) })
        : t(FAILURE_COPY[confirmFailure].body)
      : null;

  return (
    <StationSignInLayout
      title={t('stationSignIn.confirmTitle', { station: view.stationName, venue: view.venueName })}
      subtitle={<Expiry text={expiry} />}
    >
      <div className="pw-form">
        <div className="pw-handoff__account">
          <p className="pw-handoff__who">{t('stationSignIn.confirm.signedInAs', { name: accountName(session.data) })}</p>
          <p className="pw-subtle pw-handoff__note">{t('stationSignIn.confirm.onlyHere')}</p>
        </div>
        <Failure message={transientFailure ?? (signOut.isError ? problem.text(signOut.error, { fallback: t('stationSignIn.failure.signOut') }) : null)} />
        <Button variant="primary" size="lg" block busy={confirm.isPending} onClick={() => confirm.mutate()}>
          {t('stationSignIn.confirm.start', { station: view.stationName })}
        </Button>
        <Button variant="secondary" block disabled={confirm.isPending} onClick={cancel}>
          {t('stationSignIn.confirm.cancel')}
        </Button>
        <Button variant="ghost" block busy={signOut.isPending} disabled={confirm.isPending} onClick={() => signOut.mutate()}>
          {t('stationSignIn.confirm.switchAccount')}
        </Button>
      </div>
    </StationSignInLayout>
  );
}

function StationSignInLayout({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  // No way off the page from the brand: inside the PC's sign-in window this is the whole task.
  return (
    <AuthLayout title={title} subtitle={subtitle} homeLink={false}>
      {children}
    </AuthLayout>
  );
}

function Expiry({ text }: { readonly text: string }): ReactNode {
  // A timer is not announced every second (role="timer" is not live by default).
  return (
    <span role="timer" className="pw-handoff__expiry">
      {text}
    </span>
  );
}

/** Where every way in brings the player back to: this page, the secret kept in this tab. */
const BACK_HERE = '/station-sign-in';

/**
 * A provider round trip from this page (the sign-in page's own, `social.tsx`): back here when it worked; a new account
 * through "Complete your profile" first, then back here; a refusal back here with `?error=` and `?provider=`.
 */
export const stationSignInTrip = (): SocialRoundTrip => ({
  landing: BACK_HERE,
  newUserLanding: withQuery('/welcome', { redirect: BACK_HERE }),
  onError: BACK_HERE,
});

/** The providers that are on, the one the player tapped at the PC first; the rest in the usual order. */
export function orderWithTapped(providers: readonly SocialProvider[], tapped: StationSignInProvider | undefined): readonly SocialProvider[] {
  const on = STATION_SIGN_IN_PROVIDERS.filter((provider) => providers.includes(provider));
  return tapped !== undefined && on.includes(tapped) ? [tapped, ...on.filter((provider) => provider !== tapped)] : on;
}

/**
 * The ways in, for a signed-out player: the sign-in page's Google, Discord and Steam buttons, right here, with the one
 * tapped at the PC first and full width; then the sign-in page itself (a password, a code to the phone) and sign-up.
 * Every one of them brings the player back here signed in. A provider account with no Clavian account comes back
 * `CONSENT_REQUIRED` and gets the sign-in page's "Create your Clavian account" here, then "Complete your profile".
 */
function SignInWays({
  provider,
  error,
  returned,
}: {
  readonly provider: StationSignInProvider | undefined;
  readonly error: string | undefined;
  readonly returned: string | undefined;
}): ReactNode {
  const { t } = useTranslation('player');
  const { providers } = useSocialProviders();
  const refusal = socialRefusal(error);
  const back = isSocialProvider(returned) ? returned : undefined;
  const [creating, setCreating] = useState<SocialProvider | null>(refusal === 'noAccount' && back !== undefined ? back : null);
  const ordered = orderWithTapped(providers, provider);
  const tapped = provider !== undefined && ordered[0] === provider ? provider : undefined;
  const others = tapped === undefined ? ordered : ordered.slice(1);

  const refusalMessage =
    refusal === undefined || creating !== null
      ? null
      : refusal === 'banned'
        ? t('signIn.failure.banned')
        : back === undefined
          ? t(`signInError.error.${refusal}`)
          : t(`social.error.${refusal}`, { provider: t(`social.provider.${back}`) });

  return (
    <>
      <Failure message={refusalMessage} />
      {tapped === undefined ? null : <TappedProviderButton provider={tapped} />}
      {others.length === 0 ? null : <SocialButtons providers={others} trip={stationSignInTrip} />}
      {ordered.length === 0 ? null : <SocialDivider />}
      <Button asChild variant={ordered.length === 0 ? 'primary' : 'secondary'} size="lg" block>
        <Link to="/sign-in" search={{ redirect: BACK_HERE, ...(provider === undefined ? {} : { provider }) }}>
          {t(ordered.length === 0 ? 'stationSignIn.signedOut.signIn' : 'stationSignIn.signedOut.signInOtherWay')}
        </Link>
      </Button>
      {ordered.length === 0 ? null : (
        <p className="pw-auth__alt">
          <Link to="/sign-up" search={{ redirect: BACK_HERE }}>
            {t('stationSignIn.signedOut.createAccount')}
          </Link>
        </p>
      )}
      <CreateAccountDialog provider={creating} trip={stationSignInTrip} onClose={() => setCreating(null)} />
    </>
  );
}

/** The provider the player tapped at the PC: the kit's full-width "Continue with Google", first on the page. */
function TappedProviderButton({ provider }: { readonly provider: SocialProvider }): ReactNode {
  const { t } = useTranslation('player');
  const { start, starting, failed, reason } = useStartSocial();
  return (
    <div className="pw-social">
      <SocialButton provider={provider} size="lg" busy={starting === provider} onClick={() => void start(provider, stationSignInTrip())}>
        {t(`social.continue.${provider}`)}
      </SocialButton>
      {failed === null ? null : (
        <p className="pw-alert" role="alert">
          {reason ?? t('social.start', { provider: t(`social.provider.${failed}`) })}
        </p>
      )}
    </div>
  );
}

/**
 * The PC's window after a provider round trip that did not sign in: what happened, in words, and the same provider again
 * (the sign-in page's other ways below it). A provider account with no Clavian account yet gets "Create your Clavian
 * account" here, then the same provider again with the terms agreed.
 */
function WindowRetry({
  provider,
  error,
  returned,
  social,
}: {
  readonly provider: StationSignInProvider;
  readonly error: string | undefined;
  readonly returned: StationSignInProvider | undefined;
  readonly social: ReturnType<typeof useStartSocial>;
}): ReactNode {
  const { t } = useTranslation('player');
  const refusal = socialRefusal(error);
  const [creating, setCreating] = useState<SocialProvider | null>(refusal === 'noAccount' ? (returned ?? provider) : null);
  const name = t(`social.provider.${provider}`);
  const message =
    social.failed !== null
      ? (social.reason ?? t('social.start', { provider: name }))
      : refusal === undefined || creating !== null || refusal === 'noAccount'
        ? null
        : refusal === 'banned'
          ? t('signIn.failure.banned')
          : t(`social.error.${refusal}`, { provider: name });
  return (
    <>
      <Failure message={message} />
      <div className="pw-social">
        <SocialButton provider={provider} size="lg" busy={social.starting === provider} onClick={() => void social.start(provider, stationSignInTrip())}>
          {t(`social.continue.${provider}`)}
        </SocialButton>
      </div>
      <SocialDivider />
      <Button asChild variant="secondary" size="lg" block>
        <Link to="/sign-in" search={{ redirect: BACK_HERE, provider }}>
          {t('stationSignIn.signedOut.signInOtherWay')}
        </Link>
      </Button>
      <CreateAccountDialog provider={creating} trip={stationSignInTrip} onClose={() => setCreating(null)} />
    </>
  );
}

/** The PC's window, signed in: the PC is being signed in by itself; a failure that can pass is said with Try again. */
function WindowConfirm({
  stationName,
  confirm,
  failure,
}: {
  readonly stationName: string;
  readonly confirm: UseMutationResult<SignInHandoffView, unknown, void>;
  readonly failure: FailureKind | null;
}): ReactNode {
  const { t } = useTranslation('player');
  if (failure !== null && failure !== 'unauthenticated' && !TERMINAL.has(failure)) {
    return <FailureState failure={failure} error={confirm.error} onRetry={() => confirm.mutate()} />;
  }
  return (
    <StationSignInLayout title={t('stationSignIn.window.signingIn', { station: stationName })}>
      <p className="pw-handoff__checking" role="status">
        <Spinner size="sm" labelHidden label={t('app.loading')} />
        <span>{t('stationSignIn.window.signingIn', { station: stationName })}</span>
      </p>
    </StationSignInLayout>
  );
}

function FailureState({
  failure,
  error,
  onRetry,
  action,
}: {
  readonly failure: Exclude<FailureKind, 'unauthenticated'>;
  /** What failed: where nothing more specific than "generic" fits, the page says what the server's code says. */
  readonly error?: unknown;
  readonly onRetry?: () => void;
  readonly action?: ReactNode;
}): ReactNode {
  const { t } = useTranslation('player');
  const problem = useProblem();
  const copy = FAILURE_COPY[failure];
  const retry = onRetry !== undefined && !TERMINAL.has(failure);
  return (
    <StationSignInLayout title={t(copy.title)}>
      <div className="pw-form">
        <p className="pw-handoff__body" role="alert">
          {failure === 'generic' && error !== undefined ? problem.text(error, { fallback: t(copy.body) }) : t(copy.body)}
        </p>
        {retry ? (
          <Button variant="primary" block onClick={onRetry}>
            {t('stationSignIn.failure.retry')}
          </Button>
        ) : null}
        {action}
      </div>
    </StationSignInLayout>
  );
}
